import { api } from "./baseApi";
import type { ApiResponse } from "@/types";

// Types
export interface OutletDrawer {
  id: string;
  outletId: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface OutletDrawerWithStatus extends OutletDrawer {
  hasHistory?: boolean;
  currentSession: {
    id: string;
    status: string;
    currentBalance: number;
    openedAt: string;
    userOpened: {
      id: string;
      name: string;
      username: string;
    };
  } | null;
}

export interface ActiveDrawer {
  id: string;
  name: string;
  description: string | null;
  isOpen: boolean;
  currentSession: {
    id: string;
    currentBalance: number;
    openedAt: string;
    userOpened: {
      id: string;
      name: string;
      username: string;
    };
  } | null;
}

export interface CreateOutletDrawerParams {
  outletId: string;
  name: string;
  description?: string;
}

export interface UpdateOutletDrawerParams {
  outletId: string;
  id: string;
  name?: string;
  description?: string | null;
  isActive?: boolean;
}

export interface GetOutletDrawersParams {
  outletId: string;
  page?: number;
  limit?: number;
  isActive?: boolean;
  search?: string;
}

export interface OutletDrawersResponse {
  data: OutletDrawerWithStatus[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  outlet: {
    id: string;
    name: string;
  };
}

export const outletDrawerApi = api.injectEndpoints({
  endpoints: builder => ({
    // Get all outlet drawers for an outlet
    getOutletDrawers: builder.query<ApiResponse<OutletDrawersResponse>, GetOutletDrawersParams>({
      query: ({ outletId, ...params }) => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            searchParams.append(key, value.toString());
          }
        });
        const queryString = searchParams.toString();
        return `/outlets/${outletId}/drawers${queryString ? `?${queryString}` : ""}`;
      },
      transformResponse: (response: any) => {
        // Handle flattened response structure from server
        // Server returns: { success: true, data: [...], pagination: {...}, outlet: {...} }
        // We need: { success: true, data: { data: [...], pagination: {...}, outlet: {...} } }
        if (response?.success && Array.isArray(response.data) && response.pagination) {
          return {
            ...response,
            data: {
              data: response.data,
              pagination: response.pagination,
              outlet: response.outlet,
            },
          };
        }
        return response;
      },
      providesTags: result =>
        result?.data?.data
          ? [
            ...result.data.data.map(({ id }) => ({ type: "OutletDrawer" as const, id })),
            { type: "OutletDrawer", id: "LIST" },
          ]
          : [{ type: "OutletDrawer", id: "LIST" }],
    }),

    // Get active drawers for POS selection
    getActiveDrawers: builder.query<ApiResponse<ActiveDrawer[]>, { outletId: string }>({
      query: ({ outletId }) => `/outlets/${outletId}/drawers/active`,
      providesTags: [{ type: "OutletDrawer", id: "ACTIVE" }],
    }),

    // Get single outlet drawer
    getOutletDrawer: builder.query<
      ApiResponse<OutletDrawerWithStatus>,
      { outletId: string; id: string }
    >({
      query: ({ outletId, id }) => `/outlets/${outletId}/drawers/${id}`,
      providesTags: (_result, _error, { id }) => [{ type: "OutletDrawer", id }],
    }),

    // Create new outlet drawer
    createOutletDrawer: builder.mutation<ApiResponse<OutletDrawer>, CreateOutletDrawerParams>({
      query: ({ outletId, ...data }) => ({
        url: `/outlets/${outletId}/drawers`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "OutletDrawer", id: "LIST" }, { type: "OutletDrawer", id: "ACTIVE" }],
    }),

    // Update outlet drawer
    updateOutletDrawer: builder.mutation<ApiResponse<OutletDrawer>, UpdateOutletDrawerParams>({
      query: ({ outletId, id, ...data }) => ({
        url: `/outlets/${outletId}/drawers/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "OutletDrawer", id },
        { type: "OutletDrawer", id: "LIST" },
        { type: "OutletDrawer", id: "ACTIVE" },
      ],
    }),

    // Delete outlet drawer
    deleteOutletDrawer: builder.mutation<
      ApiResponse<{ success: boolean }>,
      { outletId: string; id: string }
    >({
      query: ({ outletId, id }) => ({
        url: `/outlets/${outletId}/drawers/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "OutletDrawer", id: "LIST" }, { type: "OutletDrawer", id: "ACTIVE" }],
    }),
  }),
});

export const {
  useGetOutletDrawersQuery,
  useGetActiveDrawersQuery,
  useGetOutletDrawerQuery,
  useCreateOutletDrawerMutation,
  useUpdateOutletDrawerMutation,
  useDeleteOutletDrawerMutation,
} = outletDrawerApi;

